import { CompanyCoreInfo, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

import { MGB_EXCLUDED_CUSTOMER_NAME_PATTERNS } from '../constants';

/** Prefer a human-readable name over a bare externalTMSID placeholder. */
function preferCustomerDisplayName(
  externalTMSID: string,
  ...candidates: Array<string | undefined>
): string {
  const id = externalTMSID.trim();
  for (const candidate of candidates) {
    const trimmed = candidate?.trim() ?? '';
    if (trimmed && trimmed !== id) {
      return trimmed;
    }
  }

  return candidates.map((c) => c?.trim() ?? '').find(Boolean) ?? id;
}

export function companyCoreInfoToTMSCustomer(
  company: CompanyCoreInfo
): TMSCustomer {
  const externalTMSID = company.externalTMSID?.trim() ?? '';
  return {
    ...company,
    externalTMSID,
    name: company.name?.trim() || externalTMSID,
    refNumber: (company as TMSCustomer).refNumber ?? '',
  };
}

function tmsCustomerSnapshotEquals(a: TMSCustomer, b: TMSCustomer): boolean {
  return (
    a.externalTMSID === b.externalTMSID &&
    a.name === b.name &&
    a.addressLine1 === b.addressLine1 &&
    a.addressLine2 === b.addressLine2 &&
    a.city === b.city &&
    a.state === b.state &&
    a.zipCode === b.zipCode &&
    a.country === b.country &&
    a.contact === b.contact &&
    a.phone === b.phone &&
    a.email === b.email &&
    a.refNumber === b.refNumber &&
    a.ediEnabled === b.ediEnabled &&
    a.isTest === b.isTest &&
    a.id === b.id &&
    a.ID === b.ID
  );
}

/** Insert or merge a customer so search/hydration can replace ID-only placeholders. */
export function upsertTMSCustomer(
  list: TMSCustomer[],
  company: CompanyCoreInfo
): TMSCustomer[] {
  const incoming = companyCoreInfoToTMSCustomer(company);
  const externalTMSID = incoming.externalTMSID?.trim();
  if (!externalTMSID) {
    return list;
  }

  const index = list.findIndex((c) => c.externalTMSID === externalTMSID);
  if (index === -1) {
    return [incoming, ...list];
  }

  const existing = list[index];
  const merged: TMSCustomer = {
    ...existing,
    ...incoming,
    name: preferCustomerDisplayName(
      externalTMSID,
      incoming.name,
      existing.name
    ),
  };

  if (tmsCustomerSnapshotEquals(existing, merged)) {
    return list;
  }

  const next = [...list];
  next[index] = merged;

  return next;
}

export function resolveCustomerDisplayName(
  customer: Pick<CompanyCoreInfo, 'externalTMSID' | 'name'>
): string {
  const externalTMSID = customer.externalTMSID?.trim() ?? '';
  return preferCustomerDisplayName(externalTMSID, customer.name, externalTMSID);
}

/** True when bill-to should follow a customer change (unset, same id, or still on prior customer). */
export function shouldMirrorBillToFromCustomerChange(
  billToExternalTMSID: string | undefined,
  newCustomerExternalTMSID: string,
  previousCustomerExternalTMSID: string | undefined
): boolean {
  const billToId = billToExternalTMSID?.trim();
  const customerId = newCustomerExternalTMSID.trim();
  const previousId = previousCustomerExternalTMSID?.trim();

  if (!customerId) {
    return false;
  }

  return (
    !billToId ||
    billToId === customerId ||
    Boolean(previousId && billToId === previousId)
  );
}

export function injectCompanyIfMissing(
  list: TMSCustomer[],
  company: CompanyCoreInfo | undefined
): TMSCustomer[] {
  const externalTMSID = company?.externalTMSID?.trim();
  if (!externalTMSID || !company) {
    return list;
  }

  return upsertTMSCustomer(list, company);
}

/** Bill-to select options: prefer load/form bill-to over paginated customer master. */
export function customerListWithBillToOption(
  customers: Maybe<TMSCustomer[]>,
  billTo: CompanyCoreInfo | undefined,
  customer?: CompanyCoreInfo | undefined
): TMSCustomer[] {
  const list = customers ?? [];
  const externalTMSID = billTo?.externalTMSID?.trim();
  if (!externalTMSID || !billTo) {
    return list;
  }

  const fromList = list.find((c) => c.externalTMSID === externalTMSID);
  const customerId = customer?.externalTMSID?.trim();
  const isMirroredBillTo = Boolean(customerId && customerId === externalTMSID);

  let companyForOptions: CompanyCoreInfo = billTo;
  if (fromList) {
    companyForOptions = isMirroredBillTo
      ? {
          ...billTo,
          ...fromList,
          name: preferCustomerDisplayName(
            externalTMSID,
            fromList.name,
            billTo.name
          ),
        }
      : {
          ...fromList,
          ...billTo,
          name: preferCustomerDisplayName(
            externalTMSID,
            billTo.name,
            fromList.name
          ),
        };
  }

  const withoutDuplicate = list.filter(
    (c) => c.externalTMSID !== externalTMSID
  );

  return upsertTMSCustomer(withoutDuplicate, companyForOptions);
}

export function filterMGBCustomers(customers: TMSCustomer[]): TMSCustomer[] {
  return customers.filter((customer) => {
    const name = customer.name?.trim() ?? '';
    return !MGB_EXCLUDED_CUSTOMER_NAME_PATTERNS.some((pattern) =>
      pattern.test(name)
    );
  });
}

export const MGB_EDI_CUSTOMER_BLOCK_MESSAGE =
  'Loads for EDI-enabled customers arrive through EDI and cannot be built in Drumkit.';

/**
 * Create-mode dropdown filter: drop excluded names and EDI customers.
 *
 * Rows may arrive from form inject/prefill without `ediEnabled` even when the
 * canonical lookup already knows the id is EDI (hydration then skips search).
 * Pass `customersByExternalID` so a missing flag is resolved from that map and
 * EDI stubs do not reappear in the create dropdown.
 *
 * `retainExternalTMSID` keeps the currently selected row visible for the select
 * label. Typeahead replaces the list with a fresh batch that often omits that
 * id (and known-EDI hydration will not re-search), so when the retained id is
 * absent we re-inject it from the lookup rather than only keeping an in-batch row.
 */
export function filterMGBCreateCustomers(
  customers: TMSCustomer[],
  customersByExternalID: Record<string, TMSCustomer> = {},
  options?: { retainExternalTMSID?: string }
): TMSCustomer[] {
  const retainId = options?.retainExternalTMSID?.trim() ?? '';
  const filtered = filterMGBCustomers(customers).filter((customer) => {
    const id = customer.externalTMSID?.trim() ?? '';
    if (retainId && id === retainId) {
      return true;
    }
    const resolvedEdiEnabled =
      customer.ediEnabled !== undefined
        ? customer.ediEnabled
        : customersByExternalID[id]?.ediEnabled;
    return resolvedEdiEnabled !== true;
  });

  if (!retainId) {
    return filtered;
  }

  const alreadyPresent = filtered.some(
    (customer) => customer.externalTMSID?.trim() === retainId
  );
  if (alreadyPresent) {
    return filtered;
  }

  const retained =
    customersByExternalID[retainId] ??
    customers.find((customer) => customer.externalTMSID?.trim() === retainId);
  if (!retained) {
    return filtered;
  }

  return [retained, ...filtered];
}

/**
 * Merge TMS customers into a stable lookup keyed by externalTMSID.
 *
 * MGB customer data is not one-shot: it accumulates from the initial list
 * sync, typeahead search, suggestion/prefill injects, and form hydration.
 * A later partial row can omit `ediEnabled` or send the external id as the
 * display name — a blind overwrite would drop EDI blocking state or replace
 * a real name (e.g. "Volkswagen") with the id. This merge keeps the richest
 * known row per id so create-mode EDI checks and the selected-customer
 * label stay correct even when that customer is filtered out of the dropdown.
 */
export function mergeMGBCustomersByExternalID(
  existing: Record<string, TMSCustomer>,
  customers: TMSCustomer[]
): Record<string, TMSCustomer> {
  let next: Record<string, TMSCustomer> | null = null;
  for (const customer of customers) {
    const id = customer.externalTMSID?.trim();
    if (!id) {
      continue;
    }
    const current = (next ?? existing)[id];
    const ediEnabled =
      customer.ediEnabled !== undefined
        ? customer.ediEnabled
        : current?.ediEnabled;

    const merged: TMSCustomer = current
      ? {
          ...current,
          ...customer,
          ediEnabled,
          name: preferCustomerDisplayName(id, customer.name, current.name),
        }
      : { ...customer, ediEnabled };
    if (current && tmsCustomerSnapshotEquals(current, merged)) {
      continue;
    }
    if (!next) {
      next = { ...existing };
    }
    next[id] = merged;
  }
  return next ?? existing;
}

/** Resolve the selected customer for EDI checks even when create-mode dropdowns filter EDI rows. */
export function resolveMGBSelectedCustomer(
  customersByExternalID: Record<string, TMSCustomer>,
  visibleCustomers: Maybe<TMSCustomer[]>,
  watchedCustomer: Pick<TMSCustomer, 'externalTMSID' | 'ediEnabled'> | undefined
): TMSCustomer | undefined {
  const id = watchedCustomer?.externalTMSID?.trim();
  if (!id) {
    return undefined;
  }

  const fromLookup = customersByExternalID[id];
  if (fromLookup) {
    return fromLookup;
  }

  const fromVisible = visibleCustomers?.find(
    (customer) => customer.externalTMSID === id
  );
  if (fromVisible) {
    return fromVisible;
  }

  if (watchedCustomer?.ediEnabled) {
    return watchedCustomer as TMSCustomer;
  }

  return undefined;
}

export function mgbCustomerNeedsHydrationSearch(
  externalTMSID: string,
  customersByExternalID: Record<string, TMSCustomer>,
  visibleCustomers: Maybe<TMSCustomer[]>,
  isCreateMode: boolean
): boolean {
  const id = externalTMSID.trim();
  if (!id) {
    return false;
  }

  const fromLookup = customersByExternalID[id];
  const inVisibleList = (visibleCustomers ?? []).some(
    (customer) => customer.externalTMSID === id
  );

  if (isCreateMode) {
    if (typeof fromLookup?.ediEnabled !== 'boolean') {
      return true;
    }

    if (fromLookup.ediEnabled) {
      return false;
    }

    if (!inVisibleList) {
      return true;
    }

    const lookupName = fromLookup.name?.trim() ?? '';
    if (!lookupName || lookupName === id) {
      return true;
    }
    return false;
  }

  if (fromLookup) {
    return false;
  }

  return !inVisibleList;
}

export function isMGBEDICustomerSelected(
  customersByExternalID: Record<string, TMSCustomer>,
  visibleCustomers: Maybe<TMSCustomer[]>,
  watchedCustomer:
    Pick<TMSCustomer, 'externalTMSID' | 'ediEnabled'> | undefined,
  isCreateMode: boolean
): boolean {
  if (!isCreateMode) {
    return false;
  }

  return Boolean(
    resolveMGBSelectedCustomer(
      customersByExternalID,
      visibleCustomers,
      watchedCustomer
    )?.ediEnabled
  );
}

export function resolveMGBCustomerNameForOptions(
  customerId: string,
  selectedCustomerName: string | undefined,
  watchedCustomerName: string | undefined
): string {
  const id = customerId.trim();
  if (!id) {
    return '';
  }

  const selected = selectedCustomerName?.trim() ?? '';
  if (selected) {
    return selected;
  }

  const watched = watchedCustomerName?.trim() ?? '';
  if (watched && watched !== id) {
    return watched;
  }

  return id;
}
